import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSupabaseData } from '../../contexts/SupabaseDataContext';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { getSellerPath } from '../../services/storageService';
import SafeIcon from '../../common/SafeIcon';
import MultiImageManager from '../common/MultiImageManager';
import OfferingOptionsEditor from './OfferingOptionsEditor';
import CraBakeryTaxFields from './CraBakeryTaxFields';
import * as FiIcons from 'react-icons/fi';

const {
  FiPlus, FiEdit2, FiTrash2, FiX, FiShoppingBag, FiLoader,
  FiDollarSign, FiCheck, FiAlertCircle, FiLayers,
  FiChevronLeft, FiChevronRight, FiArrowRight, FiImage, FiType, FiMapPin
} = FiIcons;

const CRA_BAKERY_CLASSIFICATIONS = {
  needs_review: 'Needs review'
};

const CATEGORY_OPTION_TEMPLATES={
  cupcakes:[
    {name:'Flavour',required:true,multiple:false},
    {name:'Frosting',required:true,multiple:false},
    {name:'Filling',required:false,multiple:false},
    {name:'Topper',required:false,multiple:false},
    {name:'Custom Decoration',required:false,multiple:false},
    {name:'Individual Wrapping',required:false,multiple:false}
  ],
  cookies:[
    {name:'Cookie Type',required:true,multiple:false},
    {name:'Design Level',required:true,multiple:false},
    {name:'Extra Colours',required:false,multiple:true},
    {name:'Metallic Finishes',required:false,multiple:true},
    {name:'Logo / Print',required:false,multiple:false},
    {name:'Individual Wrapping',required:false,multiple:false}
  ],
  macarons:[
    {name:'Flavour',required:true,multiple:false},
    {name:'Stencil',required:false,multiple:false},
    {name:'Print',required:false,multiple:false},
    {name:'Gold Finish',required:false,multiple:false},
    {name:'Handwriting',required:false,multiple:false},
    {name:'Fondant Pieces',required:false,multiple:false}
  ],
  'brownies-bars':[
    {name:'Base Flavour',required:true,multiple:false},
    {name:'Fillings',required:false,multiple:true},
    {name:'Toppings',required:false,multiple:true},
    {name:'Drizzle',required:false,multiple:false},
    {name:'Gift Box',required:false,multiple:false}
  ],
  'cake-pops-cakesicles':[
    {name:'Flavour',required:true,multiple:false},
    {name:'Coating',required:true,multiple:false},
    {name:'Colour',required:false,multiple:false},
    {name:'Drizzle',required:false,multiple:false},
    {name:'Logo',required:false,multiple:false},
    {name:'Themed Decoration',required:false,multiple:false}
  ],
  donuts:[
    {name:'Type or Flavour',required:true,multiple:false},
    {name:'Filling',required:false,multiple:false},
    {name:'Glaze',required:false,multiple:false},
    {name:'Toppings',required:false,multiple:true},
    {name:'Custom Lettering',required:false,multiple:false}
  ],
  'pies-tarts':[
    {name:'Flavour or Filling',required:true,multiple:false},
    {name:'Topping',required:false,multiple:false},
    {name:'Decoration',required:false,multiple:false},
    {name:'Message',required:false,multiple:false}
  ]
};

const getCategoryTemplate=(category='')=>{
  const key=String(category)
    .toLowerCase()
    .replace(/&/g,'and')
    .replace(/bars?/g,'bars')
    .replace(/cake pops?/g,'cake-pops')
    .replace(/cakesicles?/g,'cakesicles')
    .replace(/[^a-z0-9]+/g,'-')
    .replace(/^-|-$/g,'');

  if (CATEGORY_OPTION_TEMPLATES[key]) {
    return CATEGORY_OPTION_TEMPLATES[key];
  }

  if (key.includes('brownie') || key.includes('bar')) {
    return CATEGORY_OPTION_TEMPLATES['brownies-bars'];
  }

  if (key.includes('cake-pop') || key.includes('cakesicle')) {
    return CATEGORY_OPTION_TEMPLATES['cake-pops-cakesicles'];
  }

  if (key.includes('pie') || key.includes('tart')) {
    return CATEGORY_OPTION_TEMPLATES['pies-tarts'];
  }

  return [];
};

const formatPrice = (price) => {
  if (!price) return '$0.00';
  return price.startsWith('$') ? price : `$${price}`;
};

const formatAllergenLabels = (allergens) => (
  String(allergens || '')
    .split(',')
    .map((allergen) => allergen.trim())
    .filter(Boolean)
);

const OfferingsTab = ({ seller, onNavigateToProfile }) => {
  const { user } = useSupabaseAuth();
  const { offerings, categories, addOffering, updateOffering, deleteOffering } = useSupabaseData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOffering, setEditingOffering] = useState(null);

  const sellerOfferings = offerings.filter(o => o.seller_id === seller?.id);

  // Profile Completeness Logic
  const hasBusinessName = !!seller?.business_name;
  const hasDescription = !!seller?.description && seller.description.length > 20;
  const hasLocation = !!seller?.location;
  const hasImages = seller?.profile_images?.length > 0 || !!seller?.profile_image;

  const isProfileComplete = hasBusinessName && hasDescription && hasLocation && hasImages;

  const handleEdit = (offering) => {
    setEditingOffering(offering);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingOffering(null);
  };

  if (!isProfileComplete) {
    return (
      <div className="max-w-4xl">
        <div className="mb-10">
          <h2 className="text-3xl font-black text-slate-900 tracking-tighter mb-2">
            Finish Your <span className="text-indigo-600">Setup</span>
          </h2>
          <p className="text-slate-500 font-medium">You're almost ready to start selling! Complete these steps to unlock your menu.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-6">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Required Steps</h3>

            <div className="space-y-4">
              {[
                { label: 'Business Name', status: hasBusinessName, icon: FiType },
                { label: 'Detailed Bio (min 20 chars)', status: hasDescription, icon: FiEdit2 },
                { label: 'Location / City', status: hasLocation, icon: FiMapPin },
                { label: 'Showcase Photos', status: hasImages, icon: FiImage }
              ].map((step, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-transparent">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 ${step.status ? 'bg-emerald-100 text-emerald-600' : 'bg-white text-slate-300'}`}>
                      <SafeIcon icon={step.icon} />
                    </div>
                    <span className={`text-sm font-black ${step.status ? 'text-slate-900' : 'text-slate-400'}`}>{step.label}</span>
                  </div>
                  {step.status ? (
                    <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiCheck} className="text-xs" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 border-2 border-slate-200 rounded-full" />
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={onNavigateToProfile}
              className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center group"
            >
              Go to Profile Settings <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="bg-indigo-600 rounded-[2.5rem] p-8 text-white shadow-2xl flex flex-col justify-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl" />
            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 border border-white/30">
                <SafeIcon icon={FiAlertCircle} className="text-3xl" />
              </div>
              <h3 className="text-2xl font-black mb-4 tracking-tight">Why is this required?</h3>
              <p className="text-indigo-100 font-medium leading-relaxed mb-6">
                Neighbors are more likely to order from bakers with a complete profile. A clear description and beautiful photos build the trust needed for a successful community marketplace.
              </p>
              <div className="flex items-center text-[10px] font-black uppercase tracking-widest text-indigo-200">
                <SafeIcon icon={FiCheck} className="mr-2" /> Verified Status Requirement
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tighter mb-1">
            Bakery <span className="text-indigo-600">Inventory</span>
          </h2>
          <p className="text-slate-500 font-medium">Manage your menu items (max 3 photos each).</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 active:scale-95"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Add New Item
        </button>
      </div>

      {sellerOfferings.length === 0 ? (
        <div className="text-center py-32 bg-white rounded-[3rem] border border-dashed border-slate-200">
          <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-8">
            <SafeIcon icon={FiShoppingBag} className="text-4xl text-slate-200" />
          </div>
          <h3 className="text-2xl font-black text-slate-900 mb-2">Inventory Empty</h3>
          <p className="text-slate-500 mb-10 max-w-xs mx-auto font-medium">List your first creation to start receiving orders.</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-10 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200"
          >
            Create Your First Item
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {sellerOfferings.map((offering) => (
            <OfferingInventoryCard
              key={offering.id}
              offering={offering}
              onEdit={handleEdit}
              onDelete={deleteOffering}
            />
          ))}
        </div>
      )}

      {isModalOpen && (
        <OfferingModal
          sellerId={seller?.id}
          userId={user?.id}
          offering={editingOffering}
          categories={categories}
          onClose={handleCloseModal}
          onSave={async (data) => {
            if (editingOffering) {
              await updateOffering(editingOffering.id, data);
            } else {
              await addOffering({ ...data, seller_id: seller.id });
            }
            handleCloseModal();
          }}
        />
      )}
    </div>
  );
};

const OfferingInventoryCard = ({ offering, onEdit, onDelete }) => {
  const [activeImg, setActiveImg] = useState(0);
  const images = offering.images?.length > 0 ? offering.images : ['https://via.placeholder.com/400x300'];
  const allergenLabels = formatAllergenLabels(offering.allergens);
  const optionCount = Array.isArray(offering.options)
    ? offering.options.filter((group) => group?.name && group?.values?.length).length
    : 0;
  const taxClass = offering.cra_bakery_tax_classification || 'needs_review';

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveImg((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveImg((prev) => (prev + 1) % images.length);
  };

  return (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 overflow-hidden shadow-xl shadow-slate-200/30 hover:shadow-2xl transition-all duration-500 group flex flex-col h-full">
      <div className="relative h-64 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={activeImg}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            src={images[activeImg]}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            alt={offering.title}
          />
        </AnimatePresence>

        {images.length > 1 && (
          <>
            <div className="absolute inset-y-0 left-0 w-1/4 flex items-center justify-start pl-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={handlePrev}
                className="w-8 h-8 bg-white/90 backdrop-blur-md rounded-lg flex items-center justify-center text-slate-900 shadow-lg hover:bg-white active:scale-90 transition-all"
              >
                <SafeIcon icon={FiChevronLeft} />
              </button>
            </div>
            <div className="absolute inset-y-0 right-0 w-1/4 flex items-center justify-end pr-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={handleNext}
                className="w-8 h-8 bg-white/90 backdrop-blur-md rounded-lg flex items-center justify-center text-slate-900 shadow-lg hover:bg-white active:scale-90 transition-all"
              >
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
            <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur-md text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full flex items-center">
              <SafeIcon icon={FiLayers} className="mr-1.5" />
              {images.length} Photos
            </div>
          </>
        )}

        <div className="absolute top-6 right-6 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0 transition-transform duration-500">
          <button
            onClick={() => onEdit(offering)}
            className="p-3 bg-white text-indigo-600 rounded-xl shadow-xl hover:bg-indigo-600 hover:text-white transition-all"
          >
            <SafeIcon icon={FiEdit2} />
          </button>
          <button
            onClick={() => onDelete(offering.id)}
            className="p-3 bg-white text-red-600 rounded-xl shadow-xl hover:bg-red-600 hover:text-white transition-all"
          >
            <SafeIcon icon={FiTrash2} />
          </button>
        </div>
      </div>

      <div className="p-8 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-black text-slate-900 text-xl tracking-tight leading-tight">{offering.title}</h3>
          <span className="text-indigo-600 font-black text-lg tracking-tighter whitespace-nowrap ml-4">{formatPrice(offering.base_price)}</span>
        </div>
        <p className="text-slate-500 text-sm mb-8 line-clamp-2 leading-relaxed font-medium flex-1">{offering.description}</p>
        <div className="mb-4 rounded-xl bg-amber-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-amber-800">
          Tax classification: {CRA_BAKERY_CLASSIFICATIONS[taxClass] || CRA_BAKERY_CLASSIFICATIONS.needs_review}
        </div>
        {optionCount > 0 && (
          <div className="mb-4 rounded-xl bg-indigo-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-indigo-700">
            {optionCount} customization {optionCount === 1 ? 'group' : 'groups'}
          </div>
        )}
        {allergenLabels.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-amber-700">
              Allergen Labels
            </p>
            <div className="flex flex-wrap gap-2">
              {allergenLabels.map((allergen) => (
                <span
                  key={allergen}
                  className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[10px] font-black uppercase tracking-wide text-amber-800"
                >
                  Contains {allergen}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const OfferingModal = ({ sellerId, userId, offering, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: offering?.title || '',
    description: offering?.description || '',
    flavors: offering?.flavors || '',
    ingredients: offering?.ingredients || '',
    allergens: offering?.allergens || '',
    base_price: offering?.base_price || '',
    category: offering?.category || '',
    images: offering?.images || [],
    options: offering?.options || [],
    cra_bakery_tax_classification: offering?.cra_bakery_tax_classification || 'needs_review',
    cra_servings_per_unit: offering?.cra_servings_per_unit || 1,
    cra_qualifying_packaging: offering?.cra_qualifying_packaging || false,
    cra_cake_weight_grams: offering?.cra_cake_weight_grams || null,
    cra_cake_customized: offering?.cra_cake_customized || false,
    cra_cake_individually_qualifies: offering?.cra_cake_individually_qualifies || false,
    cra_tax_review_note: offering?.cra_tax_review_note || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const templateGroups=getCategoryTemplate(formData.category);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);
    setSaveError('');
    try {
      await onSave({
        ...formData,
        cra_servings_per_unit: Number(formData.cra_servings_per_unit) || 1,
        cra_cake_weight_grams: formData.cra_cake_weight_grams
          ? Number(formData.cra_cake_weight_grams)
          : null,
        allergens: formData.allergens
          .split(',')
          .map((allergen) => allergen.trim())
          .filter(Boolean)
          .join(', '),
        images: formData.images.filter(Boolean),
        options:formData.options
          .map((group)=>({
            ...group,
            name:String(group.name || '').trim(),
            values:group.values
              .map((value)=>({
                ...value,
                label:String(value.label || '').trim(),
                price:Number(value.price) || 0,
                pricing_mode:value.pricing_mode==='once_per_order'
                  ? 'once_per_order'
                  : 'per_item'
              }))
              .filter((value)=>value.label)
          }))
          .filter((group)=>group.name && group.values.length)
      });
    } catch (error) {
      console.error('Unable to save menu item:', error);
      setSaveError(error?.message || 'Unable to save this menu item. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[3rem] max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-100 no-scrollbar"
      >
        <div className="flex items-center justify-between p-10 border-b border-slate-50 sticky top-0 bg-white z-10">
          <h3 className="text-2xl font-black text-slate-900 tracking-tighter leading-none">
            {offering ? 'Update Item' : 'New Creation'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-3 text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-2xl transition-all"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-10 space-y-10">
          <MultiImageManager
            label="Product Gallery (Max 3)"
            maxImages={3}
            path={getSellerPath(userId, 'offerings')}
            images={formData.images}
            onImagesChange={(urls) => setFormData(prev => ({ ...prev, images: urls }))}
            aspect={4 / 3}
            idealSize="800x600 (4:3)"
          />

          <div className="space-y-6">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Item Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-6 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-bold placeholder-slate-300"
                placeholder="e.g., Signature Salted Caramel Macarons"
              />
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Base Price</label>
                <div className="relative">
                  <SafeIcon icon={FiDollarSign} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" />
                  <input
                    type="text"
                    required
                    placeholder="0.00"
                    value={formData.base_price}
                    onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                    className="w-full pl-12 pr-6 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Category</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-6 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all appearance-none font-black text-xs uppercase tracking-widest text-slate-600"
                >
                  <option value="">Select</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">General Description</label>
              <textarea
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-6 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all resize-none leading-relaxed font-medium"
                placeholder="Brief summary of this item..."
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Ingredients</label>
              <textarea
                rows="2"
                value={formData.ingredients}
                onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                className="w-full px-6 py-4 bg-slate-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all resize-none leading-relaxed font-medium"
                placeholder="List the main ingredients..."
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-amber-700 uppercase tracking-widest mb-3 ml-1">Allergen Labels</label>
              <input
                type="text"
                value={formData.allergens}
                onChange={(e) => setFormData({ ...formData, allergens: e.target.value })}
                className="w-full px-6 py-4 bg-amber-50/50 border border-amber-100 rounded-2xl focus:ring-2 focus:ring-amber-400 focus:bg-white outline-none transition-all font-bold"
                placeholder="e.g., milk, eggs, wheat, peanuts"
              />
              <p className="text-xs text-slate-400 font-medium mt-2 ml-1">
                Separate each allergen with a comma. These labels will be visible to customers.
              </p>
            </div>
          </div>

          <CraBakeryTaxFields
            value={formData}
            onChange={(updates) => setFormData((current) => ({
              ...current,
              ...updates
            }))}
          />

          <OfferingOptionsEditor
            value={formData.options}
            onChange={(options)=>setFormData({
              ...formData,
              options
            })}
            templateGroups={templateGroups}
          />

          {saveError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-bold text-red-700"
            >
              <SafeIcon icon={FiAlertCircle} className="mt-0.5 shrink-0" />
              <span>{saveError}</span>
            </div>
          )}

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSaving}
              className="w-full px-8 py-5 bg-indigo-600 text-white font-black text-sm uppercase tracking-[0.2em] rounded-[1.5rem] hover:bg-indigo-700 shadow-2xl shadow-indigo-100 transition-all transform active:scale-[0.98] disabled:opacity-50"
            >
              {isSaving ? (
                <><SafeIcon icon={FiLoader} className="mr-3 animate-spin" /> Saving Item...</>
              ) : (
                'Confirm Item'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default OfferingsTab;
